import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { App } from '@galaxy/marketplace-apps';
import { Package } from '@vendasta/marketplace-packages';
import { SalesOrdersService } from '@vendasta/sales-orders';
import {
  SalesOrderLineItemSelectorDialogComponent,
  SalesOrderLineItemSelectorDialogData,
  SalesOrderLineItemSelectorDialogResult,
} from '@vendasta/sales-ui';
import { UILineItem } from '@vendasta/store';
import { lastValueFrom, Observable } from 'rxjs';
import { first, map, take, shareReplay, switchMap } from 'rxjs/operators';
import { LineItemsService } from './line-items.service';
import { AppWithRetailPricing, LineItemsSelected } from './line-items.interface';

export interface LineItemsSelectorModalInput {
  marketId?: string;
  currentLineItems: UILineItem[];
  setLineItems: (lineItems: UILineItem[]) => void;
}
@Injectable()
export class LineItemsSelectorService {
  hideSellingStandaloneProducts: boolean;

  constructor(
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private readonly dialog: MatDialog,
    private readonly lineItemsService: LineItemsService,
    private readonly salesOrdersService: SalesOrdersService,
  ) {}

  async openLineItemsSelector(input: LineItemsSelectorModalInput): Promise<UILineItem[]> {
    this.hideSellingStandaloneProducts = await lastValueFrom(this.getHideSellingStandaloneProducts(input.marketId));

    const preselectedPackages = input.currentLineItems
      .filter((item) => !!item.lineItem?.packageId)
      .map((item) => new Package({ packageId: item.lineItem.packageId }));
    const preselectedApps = input.currentLineItems
      .filter((item) => !!item.lineItem?.appKey?.appId)
      .map(
        (item) =>
          new App({
            key: {
              appId: item.lineItem.appKey.appId,
              editionId: item.lineItem.appKey.editionId,
            },
            appType: item.appType,
          }),
      );

    const dialogRef = this.dialog.open<
      SalesOrderLineItemSelectorDialogComponent,
      SalesOrderLineItemSelectorDialogData,
      SalesOrderLineItemSelectorDialogResult
    >(SalesOrderLineItemSelectorDialogComponent, {
      data: {
        marketId: input.marketId,
        preselectedPackages: preselectedPackages,
        preselectedApps: preselectedApps,
        hideSellingStandaloneProducts: this.hideSellingStandaloneProducts,
        hideOwnerName: true,
      },
      maxWidth: '100vw',
      maxHeight: '100vh',
      minWidth: '750px',
    });

    const newlySelectedLineItems = await lastValueFrom(
      dialogRef.afterClosed().pipe(
        first(),
        map((result) => {
          const items: LineItemsSelected = {
            apps: result.apps as AppWithRetailPricing[],
            packages: result.packages,
          };
          return items;
        }),
      ),
    );

    if (!newlySelectedLineItems) return input.currentLineItems;
    return lastValueFrom(
      this.lineItemsService
        .resolveLineItemSelectorInteractions(
          preselectedPackages,
          preselectedApps,
          newlySelectedLineItems,
          input.currentLineItems,
        )
        .pipe(first()),
    );
  }

  getHideSellingStandaloneProducts(marketId: string): Observable<boolean> {
    return this.partnerId$.pipe(
      switchMap((partnerId) => this.salesOrdersService.getConfig(partnerId, marketId)),
      map((c) => c.salespersonOptions.disableSellingStandaloneProducts),
      take(1),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}
